/**
 * Sprint 10 Batch 2.1: Usage Limit Banner
 * Shows warning when approaching usage limits
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, X } from 'lucide-react';
import { Button } from './ui/button';

const UsageLimitBanner = ({ 
  limitType, // 'leads' | 'salesReps' | 'offers'
  current, 
  max, 
  onDismiss,
  onUpgrade 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Don't show if unlimited (-1) or under 80% threshold
  if (max === -1 || current < max * 0.8) return null;
  
  const percentage = Math.round((current / max) * 100);
  const remaining = max - current;
  const isAtLimit = current >= max;
  const isNearLimit = percentage >= 80 && !isAtLimit;
  
  const limitLabels = {
    leads: t('limits.leads', 'leads'),
    salesReps: t('limits.salesReps', 'included users'),
    offers: t('limits.offers', 'offers this month'),
  };
  
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/clinic/packages');
    }
  };
  
  return (
    <div 
      className={`relative flex items-center justify-between px-4 py-3 rounded-lg mb-4 ${
        isAtLimit 
          ? 'bg-red-50 border border-red-200' 
          : 'bg-amber-50 border border-amber-200'
      }`}
      data-testid={`usage-limit-banner-${limitType}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isAtLimit ? 'bg-red-100' : 'bg-amber-100'}`}>
          {isAtLimit ? (
            <AlertTriangle className={`h-5 w-5 ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`} />
          ) : (
            <TrendingUp className="h-5 w-5 text-amber-600" />
          )}
        </div>
        
        <div>
          <p className={`font-medium ${isAtLimit ? 'text-red-900' : 'text-amber-900'}`}>
            {isAtLimit 
              ? t('limits.reachedTitle', '{{type}} limit reached', { type: limitLabels[limitType] })
              : t('limits.nearingTitle', 'Approaching {{type}} limit', { type: limitLabels[limitType] })
            }
          </p>
          <p className={`text-sm ${isAtLimit ? 'text-red-700' : 'text-amber-700'}`}>
            {isAtLimit 
              ? t('limits.reachedDesc', "You've used all {{max}} {{type}}. Upgrade to add more.", { max, type: limitLabels[limitType] })
              : t('limits.nearingDesc', '{{current}} of {{max}} {{type}} used ({{remaining}} remaining)', { current, max, type: limitLabels[limitType], remaining })
            }
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Button 
          size="sm"
          onClick={handleUpgrade}
          className={isAtLimit ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
          data-testid={`upgrade-from-limit-${limitType}`}
        >
          {t('limits.upgrade', 'Upgrade')}
        </Button>
        
        {onDismiss && !isAtLimit && (
          <button 
            onClick={onDismiss}
            className="p-1 hover:bg-amber-100 rounded"
            data-testid={`dismiss-limit-${limitType}`}
          >
            <X className="h-4 w-4 text-amber-600" />
          </button>
        )}
      </div>
    </div>
  );
};

export default UsageLimitBanner;
